import React, { useEffect } from "react";
import { BestTable } from "best-common-react";
import {
    DeleteFormatter,
    NameFormatter,
    DrugTestFormatter,
    ModalBody,
    BigsBestTable,
} from "./BulkCommon";
import { TitleFormatter, OrgFormatter } from "../../../adminRoster/AdminPersonnelTable";
import { useSelectedPersonnel } from "../../../../contexts/SelectedPersonnelContext";
import styled from "styled-components";
import { RequiredLabel } from "../../FormElements";
import { PrimaryButtonStyled } from "../../PrimaryButtonStyled";
import { useGlobalModal } from "../../../../contexts/GlobalModalContext";
import { OutlinedButtonStyled } from "../../OutlinedButtonStyled";
import { BigsTextArea } from "../../BigsBcrWrapper";
import { useBulkDTexemptCheck } from "../../../../customHooks/useBulkDTexemptCheck";
import { PersonnelTypes } from "../../../../constants/PersonnelTypes";

type BulkExemptPersonnelModalBodyProps = {};

const SlateText = styled.div.attrs(() => ({ className: "mt-3 mb-2" }))`
    color: var(--slate);
    font-size: 16px;
`;

const Columns = [
    {
        key: "",
        name: "",
        width: 40,
        formatter: DeleteFormatter,
    },
    {
        key: "lastName",
        name: "Name",
        sortable: true,
        formatter: NameFormatter,
        width: 135,
    },
    {
        key: "title",
        name: "Title",
        sortable: true,
        formatter: TitleFormatter,
        width: 110,
    },
    {
        key: "org",
        name: "Club",
        sortable: true,
        formatter: OrgFormatter,
        width: 75,
    },
    {
        key: "affiliateNameLevel",
        name: "Affiliate",
        sortable: true,
        width: 220,
    },
    {
        key: "",
        name: "DT",
        width: 80,
        formatter: DrugTestFormatter,
    },
];

const BulkExemptPersonnelModalBody: React.FC<BulkExemptPersonnelModalBodyProps> = ({}) => {
    const {
        initialSelectedPersonnel,
        selectedPersonnel,
        setSelectedPersonnel,
        exemptComment,
        setExemptComment,
    } = useSelectedPersonnel();

    useEffect(() => {
        return () => {
            setSelectedPersonnel([...initialSelectedPersonnel]);
            setExemptComment("");
        };
    }, []);

    useBulkDTexemptCheck(selectedPersonnel, setSelectedPersonnel);

    return (
        <ModalBody height={336}>
            <SlateText>Are you sure you want to move the following personnel to exempt?</SlateText>
            <span
                style={{
                    fontFamily: "Helvetica",
                    fontStyle: "oblique",
                    color: "#92a5ba",
                    fontSize: "0.875rem",
                }}
            >
                {selectedPersonnel.length} personnel selected
            </span>
            <BigsBestTable>
                <BestTable
                    data={selectedPersonnel}
                    columns={Columns}
                    headerHeight={30}
                    maxTableHeight={150}
                    rowHeight={40}
                    rowExpansion={{
                        expandable: false,
                    }}
                />
            </BigsBestTable>
            <RequiredLabel text="Comments" required />
            <BigsTextArea
                placeholder="enter comments..."
                value={exemptComment}
                onChange={(event: any) => {
                    setExemptComment(event.target.value);
                }}
                resize="none"
            />
        </ModalBody>
    );
};

type BulkExemptPersonnelModalFooterProps = {
    personnelAdvancedSearch(arg1: any, arg2: any, arg3: any, arg4: any): void;
    adminSelectedMenuOptions: Array<any>;
    adminAffiliationFilters: Array<any>;
    adminSearchText: Array<any> | string;
};

const BulkExemptPersonnelModalFooter: React.FC<BulkExemptPersonnelModalFooterProps> = ({
    personnelAdvancedSearch,
    adminSelectedMenuOptions,
    adminAffiliationFilters,
    adminSearchText,
}) => {
    const { closeModal } = useGlobalModal();
    const { selectedPersonnel, bulkExemptPersonnel, exemptComment } = useSelectedPersonnel();

    return (
        <div className="d-flex">
            <div className="mr-2">
                <PrimaryButtonStyled
                    width={157}
                    height={26}
                    disabled={selectedPersonnel.length === 0 || exemptComment === ""}
                    onClick={event => {
                        event.stopPropagation();
                        closeModal();
                        bulkExemptPersonnel();
                        setTimeout(
                            () =>
                                personnelAdvancedSearch(
                                    adminSearchText && adminSearchText.length !== 0
                                        ? adminSearchText
                                        : null,
                                    adminAffiliationFilters
                                        ? adminAffiliationFilters.map(opt => opt.orgId)
                                        : null,
                                    adminSelectedMenuOptions.map(opt => opt.id),
                                    PersonnelTypes.CLUB,
                                ),
                            1000,
                        );
                    }}
                >
                    Exempt Personnel
                </PrimaryButtonStyled>
            </div>

            <OutlinedButtonStyled
                width={80}
                height={26}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                }}
            >
                Cancel
            </OutlinedButtonStyled>
        </div>
    );
};

BulkExemptPersonnelModalBody.displayName = "BodyContent";
BulkExemptPersonnelModalFooter.displayName = "FooterContent";

export { BulkExemptPersonnelModalBody, BulkExemptPersonnelModalFooter };
